import React from 'react';
import './OnboardingAurora.css';

/**
 * Ambient onboarding background: slow-drifting gradient orbs, a faint dot
 * grid, a slow glass sheen — and the PROGRESS BEAM: a slim column of light
 * pinned beside the content on the right that slowly fills as the user
 * advances. White, soft-gold and teal only. The leading edge carries a
 * glowing head with a glass chip naming the current stage, and stage
 * notches light up as the beam passes them.
 *
 * Fixed, pointer-transparent, honours prefers-reduced-motion (CSS).
 */

/** The onboarding journey: Welcome → Context → Baseline → Profile →
 *  Coaching setup → Focus. Hosts with a different journey (/get-started,
 *  /try) pass their own `labels`; the beam then has one notch per label. */
export const STAGE_COUNT = 6;

export const DEFAULT_LABELS = [
  'Welcome',
  'Context',
  'Baseline',
  'Profile',
  'Coaching setup',
  'Focus',
];

interface OnboardingAuroraProps {
  /** 1..N — how far the journey (and the beam) has progressed. */
  stage: number;
  /** Optional per-stage labels; when given, their count sets the number of
   *  stages (so a seven-step host still gets seven notches). Defaults to the
   *  six onboarding steps. */
  labels?: string[];
  /** Ambient background only: no progress beam or stage label (terminal
   *  pages such as the post-checkout states, which are not a step). */
  hideBeam?: boolean;
}

const OnboardingAurora: React.FC<OnboardingAuroraProps> = ({ stage, labels, hideBeam = false }) => {
  const stageLabels = labels && labels.length >= 2 ? labels : DEFAULT_LABELS;
  const count = stageLabels.length;
  const s = Math.max(1, Math.min(count, Math.floor(Number(stage) || 1)));
  const pct = ((s - 1) / (count - 1)) * 100;
  const currentLabel = stageLabels[s - 1] || '';

  return (
    <div
      className="onb-aurora"
      data-stage={s}
      data-final={s === count ? 'true' : undefined}
      aria-hidden="true"
    >
      <div className="onb-aurora-orb onb-aurora-orb--a" />
      <div className="onb-aurora-orb onb-aurora-orb--b" />
      <div className="onb-aurora-orb onb-aurora-orb--c" />
      <div className="onb-aurora-grid" />
      <div className="onb-aurora-sheen" />

      {!hideBeam && (
        <div className="onb-beam" style={{ '--beam-pct': `${pct}%` } as React.CSSProperties}>
          <div className="onb-beam-track">
            <div className="onb-beam-fill" />
            {Array.from({ length: count }, (_, i) => (
              <span
                key={i}
                className={`onb-beam-tick${i < s ? ' is-lit' : ''}${i === s - 1 ? ' is-current' : ''}`}
                style={{ top: `${(i / (count - 1)) * 100}%` }}
              />
            ))}
            <div className="onb-beam-head" />
          </div>
          {currentLabel && (
            <div className="onb-beam-label" key={s}>
              {currentLabel}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OnboardingAurora;
